import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import Joi from 'joi';
import _ from 'lodash';

import { useGetTunnelMessagesQuery } from 'api/main';
import { useSendExperienceMutation } from 'api/tunnelSteps';
import { userSelectors, tunnelActions } from 'reducers';
import {
    getSubscriptionRoleName,
    isStatusOk,
    jsonParseSafe,
    setTunnelChangesHandlers,
    validateSchema,
} from 'utils/helpers';
import { useConfirmSuspend } from 'utils/hooks';
import { RoleNames } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { TunnelStep, FormBox } from '../../common';
import {
    CheckboxLabel,
    CustomTextField,
    ErrorMessage,
    InfoButton,
    RadioLabel,
    SubmitButton,
} from 'components';
import { Box, FormControl, FormGroup, Grid, RadioGroup, Typography } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles(theme => ({
    flexContainer: {
        flexDirection: 'row',
        display: 'flex',
        flexWrap: 'wrap',
        columnGap: 16,
    },
    optionsContainer: {
        flexDirection: 'row',
        display: 'flex',
        [theme.breakpoints.down(520)]: {
            alignItems: 'flex-start',
            flexDirection: 'column',
        },
    },
    option: {
        color: theme.palette.grey[600],
        fontSize: 16,
        display: 'table',
        alignItems: 'baseline',
        '& .MuiFormControlLabel-label': {
            display: 'table-cell',
        },
    },
    optionLabel: {
        color: theme.palette.grey[600],
        fontSize: 16,
    },
    subQuestion: {
        marginTop: theme.spacing(2),
        [theme.breakpoints.up(500)]: {
            marginLeft: 80,
        },
    },
    subLabel: {
        fontSize: 16,
        marginBottom: 10,
    },
    radioGroup: {
        display: 'flex',
        flexDirection: 'column',
    },
    label: {
        whiteSpace: 'nowrap',
        marginBottom: 0,
        marginRight: 10,
        textOverflow: 'ellipsis',
        width: 175,
    },
    instrumentsContainer: {
        flexDirection: 'column',
        display: 'flex',
        marginBottom: 10,
    },
    infoButton: { marginRight: 24 },
}));

const Experience = props => {
    const { userStep, handleNextStep, getCompletedFormTitle, kycSurvey, defaultForm_1 } = props;
    const classes = useStyles();
    const dispatch = useDispatch();

    const { data: messages } = useGetTunnelMessagesQuery();
    const [sendExperience, { isLoading: buttonLoader, error }] = useSendExperienceMutation();

    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);

    const isResubmit = useMemo(
        () => userStep?.successfulAttemptsNumber > 0 || userStep?.failedAttemptsNumber > 0,
        [userStep]
    );
    const hasStatusOk = useMemo(() => isStatusOk(userStep), [userStep]);
    const hasSuccessfulAttempt = useMemo(() => userStep?.successfulAttemptsNumber > 0, [userStep]);
    const [currentForm, setCurrentForm] = useState(0);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

    const [formStatus, setFormStatus] = useState([
        {
            status: 'started',
            isDirty: false,
            formKey: 'form_0',
            errors: {},
        },
        {
            status: 'notStarted',
            isDirty: false,
            formKey: 'form_1',
            errors: {},
        },
        {
            status: 'notStarted',
            isDirty: false,
            formKey: 'form_2',
            errors: {},
        },
    ]);

    const formTouched = useCallback(() => {
        setFormStatus(prevState => [
            ...prevState.slice(0, currentForm),
            { ...prevState[currentForm], isDirty: true },
            ...prevState.slice(currentForm + 1),
        ]);
    }, [currentForm]);

    const defaultForm_0 = useMemo(() => {
        const financialInstrument = jsonParseSafe(kycSurvey?.financialInstrument);
        const form_0_state = {
            choice: {
                'step36.question_1.option_1': false,
                'step36.question_1.option_2': false,
                'step36.question_1.option_3': false,
                'step36.question_1.option_4': false,
                'step36.question_1.option_5': false,
                'step36.question_1.option_6': false,
                'step36.question_1.option_7': false,
                'general_options.other': false,
                'general_options.none': false,
            },
            other: '',
        };

        if (financialInstrument) {
            Object.keys(form_0_state.choice).forEach(key => {
                form_0_state.choice[key] = financialInstrument.choice.indexOf(key) > -1;
            });

            form_0_state.other = financialInstrument.other;
        }

        return form_0_state;
    }, [kycSurvey]);
    const [form_0, setForm_0] = useState(defaultForm_0);

    const [form_1, setForm_1] = useState(defaultForm_1);

    const defaultForm_2 = useMemo(
        () => ({
            opcvmActionInvestment: isResubmit ? kycSurvey?.opcvmActionInvestment : '',
            opcvmObligationInvestment: isResubmit ? kycSurvey?.opcvmObligationInvestment : '',
            opcvmMonetaryInvestment: isResubmit ? kycSurvey?.opcvmMonetaryInvestment : '',
            listedShareInvestment: isResubmit ? kycSurvey?.listedShareInvestment : '',
            unlistedShareInvestment: isResubmit ? kycSurvey?.unlistedShareInvestment : '',
            otherFundInvestment: isResubmit ? kycSurvey?.otherFundInvestment : '',
            otherInvestment: isResubmit ? kycSurvey?.otherInvestment : '',
        }),
        [kycSurvey, isResubmit]
    );
    const [form_2, setForm_2] = useState(defaultForm_2);

    //partial schema for validation
    const schema = useMemo(
        () =>
            tunnelSubscription.indirectUntransferred
                ? {}
                : {
                      form_1: {
                          other: form_1.choice['step36.question_2.option_4']
                              ? Joi.string().required()
                              : Joi.any(),
                      },
                      form_2: {
                          opcvmActionInvestment: Joi.string().required(),
                          opcvmObligationInvestment: Joi.string().required(),
                          opcvmMonetaryInvestment: Joi.string().required(),
                          listedShareInvestment: Joi.string().required(),
                          unlistedShareInvestment: Joi.string().required(),
                          otherFundInvestment: Joi.string().required(),
                          otherInvestment: Joi.string().required(),
                      },
                  },
        [tunnelSubscription, form_1]
    );

    const handleForm_0 = useCallback(
        key =>
            ({ target: { name, checked, value } }) => {
                setForm_0(prevState => {
                    if (key === 'none') {
                        return {
                            ...prevState,
                            choice: {
                                'step36.question_1.option_1': false,
                                'step36.question_1.option_2': false,
                                'step36.question_1.option_3': false,
                                'step36.question_1.option_4': false,
                                'step36.question_1.option_5': false,
                                'step36.question_1.option_6': false,
                                'step36.question_1.option_7': false,
                                'general_options.other': false,
                                'general_options.none': checked,
                            },
                            other: '',
                        };
                    } else {
                        return {
                            ...prevState,
                            [key]:
                                key === 'choice'
                                    ? {
                                          ...prevState.choice,
                                          [name]: checked,
                                          'general_options.none': false,
                                      }
                                    : value,
                        };
                    }
                });
                formTouched();
            },
        [formTouched]
    );

    const handleForm_1 = useCallback(
        key =>
            ({ target: { name, checked, value } }) => {
                setForm_1(prevState => {
                    if (key !== 'choice') {
                        return { ...prevState, other: value };
                    } else {
                        return {
                            choice: {
                                ...prevState.choice,
                                [name]: checked,
                            },
                            other:
                                !checked && name === 'step36.question_2.option_4'
                                    ? ''
                                    : prevState.other,
                        };
                    }
                });
                formTouched();
            },
        [formTouched]
    );

    const handleForm_2 = useCallback(
        key =>
            ({ target: { value } }) => {
                setForm_2(prevState => ({ ...prevState, [key]: value }));
                formTouched();
            },
        [formTouched]
    );

    const formArray = useMemo(() => [form_0, form_1, form_2], [form_0, form_1, form_2]);

    const defaultFormArray = useMemo(
        () => [defaultForm_0, defaultForm_1, defaultForm_2],
        [defaultForm_2, defaultForm_0, defaultForm_1]
    );

    // set form to completed if resubmit
    useEffect(() => {
        if (isResubmit) {
            setFormStatus(prevState =>
                prevState.reduce(
                    (acc, value) => [...acc, { ...value, isDirty: true, status: 'completed' }],
                    []
                )
            );
        }
    }, [isResubmit, defaultFormArray]);

    // effect to update default forms whenever the data on the server is updated
    useEffect(() => {
        setForm_0(defaultForm_0);
        setForm_2(defaultForm_2);
        setForm_1(defaultForm_1);
    }, [defaultForm_0, defaultForm_1, defaultForm_2]);

    const validateForm_0 = useCallback(() => {
        const isValid = Object.keys(form_0.choice)
            .slice(0, 7)
            .some(option => form_0.choice[option] === true);

        if (form_0.choice['general_options.other']) {
            return !!form_0.other;
        } else if (form_0.choice['general_options.none'] && !form_0.other && !isValid) {
            return true;
        } else {
            return isValid;
        }
    }, [form_0]);

    const currentFormIsValid = useCallback(
        (formSchema, form, formKey) => {
            if (tunnelSubscription.indirectUntransferred) return true;
            const index = formStatus.findIndex(x => x.formKey === formKey);
            const { valid, newErrors } = validateSchema(formSchema, form);
            let isValid = valid;
            let errors = { ...newErrors };

            if (formKey === 'form_0') {
                isValid = validateForm_0();
            }

            if (!isValid && formKey === 'form_0') {
                errors[formKey] = {
                    ...errors,
                    checkbox: texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                };
                if (form.choice['general_options.other'] && !form.other) {
                    errors[formKey] = {
                        input: texts.REMPLIER_CETT_ENTREE,
                    };
                }
            }

            if (!isValid && formKey === 'form_1') {
                if (form.choice['step36.question_2.option_4'] && !form.other) {
                    errors[formKey] = {
                        other: texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                    };
                }
            }

            if (!isValid && formKey === 'form_2') {
                errors['radio'] = texts.CHOISIR_UNE_OPTION;
            }

            setFormStatus(prevState => [
                ...prevState.slice(0, index),
                {
                    ...prevState[index],
                    errors: isValid ? {} : errors,
                },
                ...prevState.slice(index + 1),
            ]);

            return isValid;
        },
        [validateForm_0, formStatus, tunnelSubscription]
    );

    // enable submit button if last form is valid
    useEffect(() => {
        if (currentFormIsValid(schema['form_2'], form_2, 'form_2')) {
            setIsSubmitDisabled(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form_2]);

    const handleFormBox = useCallback(
        key => () => {
            const formIndex = formStatus.findIndex(x => x.formKey === key);
            let untouchedFormIndex = formStatus.findIndex(form => form.status === 'notStarted');
            untouchedFormIndex = untouchedFormIndex !== -1 ? untouchedFormIndex : formIndex + 1;

            if (currentFormIsValid(schema[key], formArray[formIndex], key)) {
                setCurrentForm(untouchedFormIndex);
                setFormStatus(prevState => {
                    if (untouchedFormIndex !== -1) {
                        return [
                            ...prevState.slice(0, formIndex),
                            { ...prevState[formIndex], status: 'completed', isDirty: true },
                            ...prevState.slice(formIndex + 1, untouchedFormIndex),
                            {
                                ...prevState[untouchedFormIndex],
                                status: 'started',
                                isDirty: false,
                            },
                            ...prevState.slice(untouchedFormIndex + 1),
                        ];
                    } else {
                        return [
                            ...prevState.slice(0, formIndex),
                            { ...prevState[formIndex], status: 'completed', isDirty: true },
                            {
                                ...prevState[formIndex + 1],
                                status: 'started',
                                isDirty: true,
                            },
                            ...prevState.slice(formIndex + 2),
                        ];
                    }
                });
            }
        },
        [currentFormIsValid, formStatus, schema, formArray]
    );

    const handleEditFormBox = key => () => {
        const formId = formStatus.findIndex(x => x.formKey === key); // ID of the form that is clicked
        const currentFormKey = formStatus[currentForm].formKey;
        const allFormsCompleted = !formStatus.find(x => x.status !== 'completed');
        if (allFormsCompleted) {
            setFormStatus(prevState => {
                return [
                    ...prevState.slice(0, formId),
                    {
                        ...prevState[formId],
                        status: 'started',
                        isDirty: true,
                    },
                    ...prevState.slice(formId + 1),
                ];
            });
            setCurrentForm(formId);
            return;
        }

        if (currentFormIsValid(schema[currentFormKey], formArray[currentForm], currentFormKey)) {
            setFormStatus(prevState => {
                if (formId > currentForm) {
                    return [
                        ...prevState.slice(0, currentForm),
                        {
                            ...prevState[currentForm],
                            status: 'completed',
                            isDirty: true,
                        },
                        ...prevState.slice(currentForm + 1, formId),
                        { ...prevState[formId], status: 'started', isDirty: true },
                        ...prevState.slice(formId + 1),
                    ];
                } else {
                    return [
                        ...prevState.slice(0, formId),
                        {
                            ...prevState[formId],
                            status: 'started',
                            isDirty: true,
                        },
                        ...prevState.slice(formId + 1, currentForm),
                        { ...prevState[currentForm], status: 'completed', isDirty: true },
                        ...prevState.slice(currentForm + 1),
                    ];
                }
            });
            setCurrentForm(formId);
        }
    };

    const everyFormIsValid = useCallback(() => {
        if (tunnelSubscription.indirectUntransferred) return true;
        return formStatus.every((form, i) =>
            currentFormIsValid(schema[form.formKey], formArray[i], form.formKey)
        );
    }, [currentFormIsValid, formStatus, formArray, schema, tunnelSubscription]);

    const handleConfirmSuspend = useConfirmSuspend();

    const handleSubmit = useCallback(
        afterSubmitCallback => () => {
            if (everyFormIsValid()) {
                sendExperience({
                    payload: { form_0, form_1, form_2 },
                    callback: () => {
                        if (afterSubmitCallback) {
                            afterSubmitCallback();
                            return;
                        }
                        handleNextStep(true);
                    },
                });
            }
        },
        [form_0, form_1, form_2, sendExperience, handleNextStep, everyFormIsValid]
    );

    const checkChanges = useCallback(() => {
        return !_.isEqualWith(defaultFormArray, formArray, (objValue, othValue) => {
            return objValue.every((formObject, i) => _.isEqual(formObject, othValue[i]));
        });
    }, [formArray, defaultFormArray]);

    const changesMade = useMemo(() => checkChanges(), [checkChanges]);

    useEffect(
        () => setTunnelChangesHandlers(handleSubmit, checkChanges, hasSuccessfulAttempt),
        [handleSubmit, checkChanges, hasSuccessfulAttempt]
    );

    useEffect(() => {
        if (error?.status === 422) {
            dispatch(
                tunnelActions.setIneligible({
                    open: true,
                    abandon: false,
                    canConfirm: true,
                    message: texts.VOTRE_RESPONSE_NE_SEMBLENT,
                    confirmMessage: texts.PAUSE_AND_WAIT,
                    onConfirm: handleConfirmSuspend,
                })
            );
        }
    }, [dispatch, error?.status, handleConfirmSuspend]);

    return (
        <TunnelStep
            title={texts.EXPERIENCE}
            subtitle={
                <>
                    <Typography variant="subtitle1">{texts.EXPERIENCE_SUBTITLE}</Typography>
                    {getSubscriptionRoleName(tunnelSubscription) === RoleNames.PM_PROFESSIONNEL && (
                        <Typography variant="subtitle1">{texts.AUTRE_PERSONNE_MORRALE}</Typography>
                    )}
                </>
            }
        >
            <Grid container direction="column" justifyContent="center">
                <FormBox
                    status={formStatus[0].status}
                    label={messages['step36.question_1.label']}
                    infoContent={texts.INSTRUMENTS_FINANCIERS_INFO}
                    infoTitle={texts.INSTRUMENTS_FINANCIERS}
                    completedLabel={getCompletedFormTitle(form_0, 'form_4')}
                    handleFormBox={handleFormBox('form_0')}
                    handleEditFormBox={handleEditFormBox('form_0')}
                >
                    <FormGroup className={classes.flexContainer}>
                        {[1, 2, 3, 4, 5, 6, 7].map(i => (
                            <React.Fragment key={`step36.question_1.option_${i}`}>
                                <Box display="flex" flexDirection="row" flexWrap="nowrap">
                                    <CheckboxLabel
                                        className={classes.option}
                                        name={`step36.question_1.option_${i}`}
                                        label={
                                            <React.Fragment>
                                                {messages[`step36.question_1.option_${i}`]}
                                                {[1, 2, 3].includes(i) && (
                                                    <InfoButton
                                                        disableMarginBottom
                                                        title={i === 1 ? texts.OPCVM : texts.FIA}
                                                        content={
                                                            i === 1
                                                                ? texts.PRODUIT_FINANCIER_AYANT
                                                                : texts.CAPITAUX_AUPRES
                                                        }
                                                    />
                                                )}
                                            </React.Fragment>
                                        }
                                        checked={form_0.choice[`step36.question_1.option_${i}`]}
                                        onChange={handleForm_0('choice')}
                                    />
                                </Box>
                            </React.Fragment>
                        ))}
                        <CheckboxLabel
                            name="general_options.none"
                            label={messages['general_options.none']}
                            className={classes.option}
                            onChange={handleForm_0('none')}
                            checked={form_0.choice['general_options.none']}
                        />
                        <CheckboxLabel
                            name="general_options.other"
                            label={messages['general_options.other']}
                            className={classes.option}
                            onChange={handleForm_0('choice')}
                            checked={form_0.choice['general_options.other']}
                        />
                        {form_0.choice['general_options.other'] && (
                            <CustomTextField
                                onChange={handleForm_0('other')}
                                value={form_0.other}
                                showError={!!formStatus[0].errors['form_0']?.input}
                                error={formStatus[0].errors['form_0']?.input}
                            />
                        )}
                    </FormGroup>
                    {!!formStatus[0].errors['form_0']?.checkbox && (
                        <ErrorMessage message={formStatus[0].errors['form_0'].checkbox} />
                    )}
                </FormBox>
                <FormBox
                    handleEditFormBox={handleEditFormBox('form_1')}
                    handleFormBox={handleFormBox('form_1')}
                    showInfoButton={false}
                    status={formStatus[1].status}
                    label={messages['step36.question_2.label']}
                    completedLabel={getCompletedFormTitle(form_1, 'form_36')}
                >
                    <FormGroup className={classes.flexContainer}>
                        {[1, 2, 3, 4].map(i => (
                            <CheckboxLabel
                                className={classes.option}
                                label={messages[`step36.question_2.option_${i}.label`]}
                                checked={form_1.choice[`step36.question_2.option_${i}`]}
                                onChange={handleForm_1('choice')}
                                key={`step36.question_2.option_${i}`}
                                name={`step36.question_2.option_${i}`}
                            />
                        ))}
                        {form_1.choice['step36.question_2.option_4'] && (
                            <FormControl component="fieldset" className={classes.subQuestion}>
                                <label className={classes.subLabel}>
                                    {messages['step36.question_2.option_4.sub_label']}
                                </label>
                                <RadioGroup
                                    className={classes.radioGroup}
                                    value={form_1.other}
                                    onChange={handleForm_1('other')}
                                >
                                    {[1, 2, 3].map(i => (
                                        <RadioLabel
                                            key={i}
                                            value={`step36.question_2.option_4.option_${i}`}
                                            label={
                                                messages[`step36.question_2.option_4.option_${i}`]
                                            }
                                            labelPlacement="end"
                                        />
                                    ))}
                                </RadioGroup>
                                {!!formStatus[1].errors['form_1']?.other && (
                                    <ErrorMessage message={formStatus[1].errors['form_1'].other} />
                                )}
                            </FormControl>
                        )}
                        {!!formStatus[1].errors['form_1']?.checkbox && (
                            <ErrorMessage message={formStatus[1].errors['form_1'].checkbox} />
                        )}
                    </FormGroup>
                </FormBox>
                <FormBox
                    isLast
                    handleEditFormBox={handleEditFormBox('form_2')}
                    handleFormBox={handleFormBox('form_2')}
                    infoTitle={texts.INVESTISSEMENTS_REALISES}
                    infoContent={texts.INVESTISSEMENTS_REALISES_INFO}
                    status={formStatus[2].status}
                    label={messages['step36.question_3.label']}
                    completedLabel={getCompletedFormTitle(form_2, 'form_5')}
                >
                    {Object.keys(form_2).map((formState, i) => (
                        <RadioGroup
                            key={formState}
                            className={classes.instrumentsContainer}
                            onChange={handleForm_2(formState)}
                        >
                            <Typography
                                className={clsx(classes.label, classes.optionLabel)}
                                variant="body1"
                                gutterBottom
                            >
                                {messages[`step36.question_${i + 4}.label`]}
                            </Typography>
                            <div className={classes.optionsContainer}>
                                <RadioLabel
                                    className={classes.option}
                                    value="financial_instruments_options.no_orders"
                                    label={messages['financial_instruments_options.no_orders']}
                                    checked={
                                        form_2[formState] ===
                                        'financial_instruments_options.no_orders'
                                    }
                                />
                                <RadioLabel
                                    className={classes.option}
                                    value="financial_instruments_options.between_1_and_3_orders"
                                    label={
                                        messages[
                                            'financial_instruments_options.between_1_and_3_orders'
                                        ]
                                    }
                                    checked={
                                        form_2[formState] ===
                                        'financial_instruments_options.between_1_and_3_orders'
                                    }
                                />
                                <RadioLabel
                                    className={classes.option}
                                    value="financial_instruments_options.more_than_3_orders"
                                    label={
                                        messages['financial_instruments_options.more_than_3_orders']
                                    }
                                    checked={
                                        form_2[formState] ===
                                        'financial_instruments_options.more_than_3_orders'
                                    }
                                />
                            </div>
                        </RadioGroup>
                    ))}
                </FormBox>
            </Grid>
            <SubmitButton
                text={hasSuccessfulAttempt && changesMade ? texts.SAVE_AND_CONTINUE : texts.SUIVANT}
                disabled={isSubmitDisabled || buttonLoader}
                onClick={changesMade || !hasStatusOk ? handleSubmit() : handleNextStep}
                loading={buttonLoader}
            />
        </TunnelStep>
    );
};

export default Experience;
